import React, { FC } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { Values } from '@fiverr-private/dropdown';
import { I18n, translate as t } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';
import { Option, Select } from '@fiverr-private/forms';
import { Text } from '@fiverr-private/typography';
import { buildDate, T_FORM_KEY } from '../../../utils';
import { DatesFieldProps, WorkExperienceFormType } from '../../../shared/types';
import { years } from '../defaults';

interface YearsProps extends Omit<DatesFieldProps, 'clearErrors' | 'setError'> {
    isStart?: boolean;
}

const Years: FC<YearsProps> = ({ isStart, errors, setValue, getValues, trigger, control, currentlyWorking }) => {
    const handleYearChange = (
        value: Values,
        onChange: ControllerRenderProps<WorkExperienceFormType>['onChange'],
        isStart?: boolean
    ) => {
        if (!value || !value.item) {
            return;
        }

        const updatedYear = value.item as string;
        const startMonth = getValues('startMonth');
        const endMonth = getValues('endMonth');

        onChange(updatedYear);
        if (isStart && startMonth) {
            setValue('startDate', buildDate(updatedYear, startMonth));
            trigger('startMonth');
            if (endMonth) {
                trigger('startMonth');
            }
        } else if (endMonth) {
            setValue('endDate', buildDate(updatedYear, endMonth));
            trigger('endMonth');
            if (startMonth) {
                trigger('startMonth');
            }
        }
    };

    return (
        <Container flex={1} data-testid={`${isStart ? 'start' : 'end'}-year`}>
            <Controller
                name={isStart ? 'startYear' : 'endYear'}
                control={control}
                rules={{ required: isStart || !currentlyWorking }}
                render={({ field: { onChange, value } }) => (
                    <Select
                        disabled={!isStart && currentlyWorking}
                        fullWidth
                        size="lg"
                        values={{ item: value }}
                        placeholder={t(`${T_FORM_KEY}.fields.dates.placeholder_year`)}
                        isError={
                            isStart
                                ? !!errors?.startYear || errors?.startMonth?.type === 'validate'
                                : !!errors?.endYear || errors?.endMonth?.type === 'validate'
                        }
                        onChange={(value) => handleYearChange(value, onChange, isStart)}
                    >
                        {years.map((yearDate, index) => (
                            <Option value={yearDate.getFullYear().toString()} key={index}>
                                {yearDate.getFullYear()}
                            </Option>
                        ))}
                    </Select>
                )}
            />
            <Text
                color="validationError"
                marginTop="2"
                hidden={isStart ? !errors?.startYear : !errors?.endYear || currentlyWorking}
            >
                {errors?.[isStart ? 'startYear' : 'endYear']?.type === 'required' && (
                    <I18n k={`${T_FORM_KEY}.fields.dates.${isStart ? 'start' : 'end'}_year_required_error`} />
                )}
            </Text>
        </Container>
    );
};

export default Years;
